import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import {
  fetchProductById,
  deleteProduct,
  clearError,
  clearSuccess,
} from "../../redux/slices/productSlice";
import { fetchCategories } from "../../redux/slices/categorySlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPencilAlt,
  FaRegTrashAlt,
  FaEye,
  FaShoppingCart,
  FaHeart,
  FaTruck,
  FaUndo,
  FaStar,
} from "react-icons/fa";
import productService from "../../services/productService";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const ProductDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { currentProduct, isLoading, error, success, isDeleting } = useSelector(
    (state) => state.products
  );

  const { categories } = useSelector((state) => state.categories);

  const category = categories.find((cat) => cat.id === currentProduct?.category_id);
  const flowType = category?.flow_type || "normal";

  const [productStats, setProductStats] = useState({
    total_cart: 0,
    total_wishlist: 0,
    total_deliveries: 0,
    total_returns: 0,
    average_rating: 0,
    total_ratings: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);



  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(id));
      fetchProductStats(id);
    }
  }, [dispatch, id]);

  const fetchProductStats = async (productId) => {
    try {
      setStatsLoading(true);
      const stats = await productService.getProductStats(productId);
      setProductStats(
        stats || {
          total_cart: 0,
          total_wishlist: 0,
          total_deliveries: 0,
          total_returns: 0,
          average_rating: 0,
          total_ratings: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching product stats:", error);
      toast.error("Failed to load product statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchCategories());
    // Clear any existing success message when component mounts
    dispatch(clearSuccess());
  }, [dispatch]);

  // Clear messages and show toasts
  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  // Cleanup effect to clear success message when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleEdit = () => {
    navigate(`/product/edit/${id}`);
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      dispatch(deleteProduct(id));
      setTimeout(() => {
        navigate("/product");
      }, 1000);
    }
  };

  const handleBack = () => {
    navigate("/product");
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  if (isLoading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!currentProduct) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>Product not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to Products
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="view-form">
      <Row>
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">Product Details</h5>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={handleBack}>
                <i className="bi bi-arrow-left-short  fs-3"></i>
              </Button>
              <Button variant="outline-warning" size="sm" onClick={handleEdit}>
                <FaPencilAlt />
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                <FaRegTrashAlt />
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <Row>
              <Col lg={9}>
                <CardBody>
                  {/* Product Information Section */}
                  <Row className="my-2 view-form">
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Product Name</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.product_name || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Status</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        <Badge
                          bg={
                            currentProduct?.product_status === "PUBLISHED"
                              ? "success"
                              : currentProduct?.product_status === "HOLD"
                                ? "warning"
                                : "danger"
                          }
                        >
                          {currentProduct?.product_status || "UNLISTED"}
                        </Badge>
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Category</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.category_name || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Subcategory</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.sub_category_name || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">MRP</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        ₹
                        {currentProduct?.mrp
                          ? parseFloat(currentProduct.mrp).toFixed(2)
                          : "0.00"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Selling Price</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.selling_price ? (
                          <>
                            <span className="text-success fw-bold">
                              ₹
                              {parseFloat(
                                currentProduct.selling_price
                              ).toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Stock Quantity</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        <div>
                          <Badge
                            bg={
                              currentProduct.stock_quantity > 10
                                ? "success"
                                : currentProduct.stock_quantity > 0
                                  ? "warning"
                                  : "danger"
                            }
                          >
                            {currentProduct.stock_quantity}{" "}
                            {currentProduct.product_type || "piece"}
                          </Badge>
                          {/* {currentProduct.stock_quantity <= 10 && currentProduct.stock_quantity > 0 && (
                        <div className="small text-warning">Low Stock</div>
                      )}
                      {currentProduct.stock_quantity === 0 && (
                        <div className="small text-danger">Out of Stock</div>
                      )} */}
                        </div>
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Discount Percentage</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.discount_percentage !== undefined && currentProduct?.discount_percentage !== null
                          ? `${parseFloat(currentProduct.discount_percentage).toFixed(2)}%`
                          : "0.00%"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Vendor</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.vendor_name || "Admin"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Created Date</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.createddate
                          ? formatDateToDDMMYYYY(currentProduct.createddate)
                          : "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Last Updated</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentProduct?.lastmodifieddate
                          ? formatDateToDDMMYYYY(currentProduct.lastmodifieddate)
                          : "-"}
                      </p>
                    </Col>
                  </Row>

                  {/* Description Section */}
                  <Row className="my-2 view-form">
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">
                        {flowType === "normal" ? "Product SKU" : "ISBN / SKU"}
                      </label>
                      <p className="mb-0 border-bottom pb-1">{currentProduct?.product_sku || "-"}</p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">
                        {flowType === "normal" ? "Brand" : "Publisher / Brand"}
                      </label>
                      <p className="mb-0 border-bottom pb-1">{currentProduct?.brand_publisher || "-"}</p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Language</label>
                      <p className="mb-0 border-bottom pb-1">{currentProduct?.book_language || "-"}</p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">GST %</label>
                      <p className="mb-0 border-bottom pb-1">{currentProduct?.gst_percentage || "0"}%</p>
                    </Col>

                    {/* School Flow Fields */}
                    {flowType === "school" && (
                      <>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Board</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.board_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Class</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.class_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Subject</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.subject_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Author Name</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.author_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Edition / Year</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.edition || "-"}</p>
                        </Col>
                      </>
                    )}

                    {/* College Flow Fields */}
                    {flowType === "college" && (
                      <>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">University</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.university_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Course</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.course_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Semester</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.semester_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Subject</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.subject_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Author Name</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.author_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Edition / Year</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.edition || "-"}</p>
                        </Col>
                      </>
                    )}

                    {/* Competitive Flow Fields */}
                    {flowType === "competitive" && (
                      <>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Exam Category</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.exam_category_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Exam</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.exam_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Competitive Subject</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.competitive_subject_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Author Name</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.author_name || "-"}</p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Edition / Year</label>
                          <p className="mb-0 border-bottom pb-1">{currentProduct?.edition || "-"}</p>
                        </Col>
                      </>
                    )}
                    <Col lg={12} className="my-2">
                      <label className="fw-bold">Description</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                          minHeight: "40px",
                        }}
                      >
                        {currentProduct?.product_description ||
                          "No description provided"}
                      </p>
                    </Col>
                  </Row>



                  {/* Product Images Section */}
                  <Row className="mx-3 my-2 view-form">
                    <Col lg={12} className="my-2">
                      <label className="fw-bold">
                        Product Images
                        {(() => {
                          let imageUrls = [];
                          try {
                            imageUrls = typeof currentProduct?.image_url === 'string'
                              ? JSON.parse(currentProduct.image_url)
                              : (currentProduct?.image_url || []);
                          } catch (e) {
                            imageUrls = [currentProduct?.image_url];
                          }

                          return imageUrls.length > 1
                            ? ` (${imageUrls.length})`
                            : "";
                        })()}
                      </label>
                      <div
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "15px",
                        }}
                      >
                        {(() => {
                          let imageUrls = [];
                          try {
                            imageUrls = typeof currentProduct?.image_url === 'string'
                              ? JSON.parse(currentProduct.image_url)
                              : (currentProduct?.image_url || []);
                          } catch (e) {
                            imageUrls = currentProduct?.image_url ? [currentProduct?.image_url] : [];
                          }

                          return imageUrls.length > 0 ? (
                            <div className="mt-2">
                              <div className="row g-2">
                                {imageUrls.map((imageUrl, index) => (
                                  <div
                                    key={index}
                                    className="col-6 col-md-4 col-lg-2"
                                  >
                                    <div className="border rounded p-2">
                                      <img
                                        src={imageUrl || process.env.PUBLIC_URL + "/not-img-found.png"} // ✅ fallback if empty or null
                                        alt={`${currentProduct?.product_name || "Product"
                                          } - Image ${index + 1}`}
                                        style={{
                                          width: "100%",
                                          height: "120px",
                                          objectFit: "cover",
                                          borderRadius: "4px",
                                          border: "1px solid #dee2e6",
                                        }}
                                        onError={(e) => {
                                          console.log(
                                            "Image load error:",
                                            imageUrl
                                          );
                                          e.target.onerror = null; // prevent infinite loop
                                          e.target.src = process.env.PUBLIC_URL + "/not-img-found.png"; // ✅ fallback if broken
                                        }}
                                      />
                                      <small className="text-muted d-block mt-1 text-center">
                                        Image {index + 1}
                                      </small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted mt-2">
                              <i className="fas fa-image fa-2x mb-2"></i>
                              <p>No images available</p>
                            </div>
                          );
                        })()}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Col>

              <Col lg={3}>
                <CardBody>
                  <label className="fw-bold mb-3 d-block">
                    Product Dashboard
                  </label>

                  {statsLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-2 text-muted small">Loading stats...</p>
                    </div>
                  ) : (
                    <div className="product-stats">
                      {/* Cart Count */}
                      <div className="stat-item mb-3 p-2 border rounded">
                        <div className="d-flex align-items-center">
                          <div
                            className="stat-icon me-2"
                            style={{ color: "#0d6efd" }}
                          >
                            <FaShoppingCart size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="stat-label text-muted small">
                              In Cart
                            </div>
                            <div className="stat-value fw-bold">
                              {productStats.total_cart}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Wishlist Count */}
                      <div className="stat-item mb-3 p-2 border rounded">
                        <div className="d-flex align-items-center">
                          <div
                            className="stat-icon me-2"
                            style={{ color: "#dc3545" }}
                          >
                            <FaHeart size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="stat-label text-muted small">
                              In Wishlist
                            </div>
                            <div className="stat-value fw-bold">
                              {productStats.total_wishlist}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Deliveries */}
                      <div className="stat-item mb-3 p-2 border rounded">
                        <div className="d-flex align-items-center">
                          <div
                            className="stat-icon me-2"
                            style={{ color: "#198754" }}
                          >
                            <FaTruck size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="stat-label text-muted small">
                              Deliveries
                            </div>
                            <div className="stat-value fw-bold">
                              {productStats.total_deliveries}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Returns */}
                      <div className="stat-item mb-3 p-2 border rounded">
                        <div className="d-flex align-items-center">
                          <div
                            className="stat-icon me-2"
                            style={{ color: "#ffc107" }}
                          >
                            <FaUndo size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="stat-label text-muted small">
                              Returns
                            </div>
                            <div className="stat-value fw-bold">
                              {productStats.total_returns}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Average Rating */}
                      <div className="stat-item mb-3 p-2 border rounded">
                        <div className="d-flex align-items-center">
                          <div
                            className="stat-icon me-2"
                            style={{ color: "#fd7e14" }}
                          >
                            <FaStar size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="stat-label text-muted small">
                              Average Rating
                            </div>
                            <div className="stat-value fw-bold">
                              {productStats.average_rating > 0
                                ? productStats.average_rating.toFixed(1)
                                : "N/A"}
                              {productStats.total_ratings > 0 && (
                                <span className="text-muted small ms-1">
                                  ({productStats.total_ratings})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProductDetails;
